import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdminTenant } from './useTenants';
import { useCloseTenant } from './useOffboarding';

/**
 * Close a customer's account (slice 27) — the REVERSIBLE half of offboarding.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 CLOSE IS NOT RELEASE. This blocks the customer's access and touches NO
 * hardware; it is fully undone by Reopen. Releasing the fleet — returning the
 * trackers and SIMs to inventory — is a SEPARATE action that only appears once an
 * account is closed. The copy says so, so no one clicks this thinking it strands
 * hardware, or clicks release thinking it merely locks the view.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * form → confirm → done, following `ResetPasswordDialog` chrome. The reason is
 * REQUIRED — it is written to the `tenant.close` audit row before the act — and
 * the confirm stage restates the tenant and the reason before anything is sent,
 * because a mis-click on the wrong row of a list is the failure this guards.
 */
export function CloseAccountDialog({
  tenant,
  onClose,
}: {
  tenant: AdminTenant;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(false);
  const [done, setDone] = useState(false);
  const close = useCloseTenant(tenant.id);

  const ready = reason.trim().length > 0;

  async function submit() {
    if (!ready) return;
    setError(false);
    try {
      await close.mutateAsync({ reason: reason.trim() });
      setConfirming(false);
      setDone(true);
    } catch {
      setError(true);
      setConfirming(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('offboard.close.title', { tenant: tenant.name })}
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-black/60 p-0 sm:items-start sm:p-8"
    >
      <div className="w-full min-h-dvh rounded-none border-0 bg-raad-panel p-5 pt-[calc(1.25rem+var(--raad-safe-top))] sm:min-h-0 sm:max-w-lg sm:rounded-2xl sm:border sm:border-raad-border sm:p-6 sm:pt-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-[17px] font-semibold">
            {t('offboard.close.title', { tenant: tenant.name })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold"
          >
            {t('offboard.close.close')}
          </button>
        </div>

        {done ? (
          <div>
            <p className="rounded-lg border border-raad-primary/40 bg-raad-primary/10 p-3 text-[12.5px]">
              {t('offboard.close.done', { tenant: tenant.name })}
            </p>
            {/* The two facts the operator must carry away: access is blocked now,
                and this changed NO hardware — releasing the fleet is separate. */}
            <p className="mt-2 rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12.5px] text-raad-muted">
              {t('offboard.close.doneReversible')}
            </p>
            <p className="mt-2 rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12.5px] text-raad-muted">
              {t('offboard.close.doneNoHardware')}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary"
              >
                {t('offboard.close.doneButton')}
              </button>
            </div>
          </div>
        ) : confirming ? (
          <div>
            <p className="rounded-lg border border-raad-warn/40 bg-raad-warn/10 p-3 text-[12.5px]">
              {t('offboard.close.confirmBody', { tenant: tenant.name })}
            </p>
            <p className="mt-2 rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12.5px] text-raad-muted">
              {t('offboard.close.confirmReason', { reason: reason.trim() })}
            </p>
            {error ? (
              <p role="alert" className="mt-3 text-[12.5px] text-raad-error">
                {t('offboard.close.failed')}
              </p>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={close.isPending}
                className="min-h-10 rounded-lg border border-raad-border px-4 text-[13px] font-semibold disabled:opacity-40"
              >
                {t('offboard.close.back')}
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={close.isPending}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary disabled:opacity-40"
              >
                {close.isPending ? t('offboard.close.closing') : t('offboard.close.confirmButton')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="mb-4 rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12px] text-raad-muted">
              {t('offboard.close.intro', { tenant: tenant.name })}
            </p>

            <label className="block">
              <span className="mb-1 block text-[12px] font-medium text-raad-muted">
                {t('offboard.close.reason')}
              </span>
              <textarea
                className="w-full min-h-20 rounded-lg border border-raad-border bg-transparent px-3 py-2 text-[13.5px] outline-none focus:border-raad-primary"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                autoFocus
                placeholder={t('offboard.close.reasonPlaceholder')}
              />
            </label>
            <span className="mt-1 block text-[11.5px] text-raad-muted">
              {t('offboard.close.reasonHint')}
            </span>

            {error ? (
              <p role="alert" className="mt-3 text-[12.5px] text-raad-error">
                {t('offboard.close.failed')}
              </p>
            ) : null}

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setError(false);
                  setConfirming(true);
                }}
                disabled={!ready}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary disabled:opacity-40"
              >
                {t('offboard.close.review')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
